/**
 * 聊天消息上下文类型
 */
export enum ChatContextType {
  // 项目设置相关
  WORLDVIEW = 'worldview',
  WRITING_STYLE = 'writing_style',
  ART_STYLE = 'art_style',
  STORY_SYNOPSIS = 'story_synopsis',
  EPISODE_OUTLINE = 'episode_outline',
  SUBJECT_SETTING = 'subject_setting',
  SCRIPT = 'script',

  // 脚本生成步骤相关（与 ScriptStepContextType 对应）
  SCRIPT_STORY_SUMMARY = 'script_story_summary',    // Step 1: 故事概要
  SCRIPT_VISUAL_STYLE = 'script_visual_style',      // Step 2: 视觉风格
  SCRIPT_CHARACTERS = 'script_characters',          // Step 3: 角色设计
  SCRIPT_PROPS = 'script_props',                    // Step 4: 道具设计
  SCRIPT_SCENES = 'script_scenes',                  // Step 5: 场景设计
  SCRIPT_SHOTS = 'script_shots'                     // Step 6: 分镜脚本
}

/**
 * 聊天消息角色
 */
export enum ChatMessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system'
}

/**
 * 聊天消息接口
 */
export interface IChatMessage {
  id: string;
  projectId: string;
  contextType: ChatContextType;
  contextId?: string;  // 可选，如章节ID、大纲ID等
  role: ChatMessageRole;
  content: string;
  appliedActionIndexes?: number[];  // 已应用的操作卡片索引
  createdAt: Date;
  updatedAt: Date;
}

/**
 * 创建聊天消息请求
 */
export interface CreateChatMessageRequest {
  projectId: string;
  contextType: ChatContextType;
  contextId?: string;
  role: ChatMessageRole;
  content: string;
}

/**
 * 获取聊天消息请求
 */
export interface GetChatMessagesRequest {
  projectId: string;
  contextType: ChatContextType;
  contextId?: string;
  limit?: number;
}

/**
 * LLM 流式生成请求
 */
export interface LLMStreamRequest {
  projectId: string;
  contextType: ChatContextType;
  contextId?: string;
  prompt: string;
  // 可选：传入当前内容作为上下文
  currentContent?: string;
}

/**
 * LLM 流式生成事件类型
 */
export enum LLMStreamEventType {
  START = 'start',
  DELTA = 'delta',
  DONE = 'done',
  ERROR = 'error'
}

/**
 * LLM 流式生成事件
 */
export interface LLMStreamEvent {
  type: LLMStreamEventType;
  content?: string;
  error?: string;
}

import { I18nLabels } from './common';
import { Locale } from './locale';

/**
 * 主体设定操作类型
 */
export enum SubjectActionType {
  CREATE = 'create',   // 新增
  UPDATE = 'update',   // 修改
  DELETE = 'delete'    // 删除
}

export const SubjectActionTypeLabels: I18nLabels<SubjectActionType> = {
  [SubjectActionType.CREATE]: { [Locale.ZH_CN]: '新增', [Locale.EN_US]: 'Create' },
  [SubjectActionType.UPDATE]: { [Locale.ZH_CN]: '修改', [Locale.EN_US]: 'Update' },
  [SubjectActionType.DELETE]: { [Locale.ZH_CN]: '删除', [Locale.EN_US]: 'Delete' },
};

export const SubjectActionTypeColors: Record<SubjectActionType, string> = {
  [SubjectActionType.CREATE]: '#22C55E',  // 绿色
  [SubjectActionType.UPDATE]: '#2563EB',  // 蓝色
  [SubjectActionType.DELETE]: '#EF4444'   // 红色
};

/**
 * 主体设定操作卡片
 */
export interface SubjectActionCard {
  actionType: SubjectActionType;
  subjectId?: string;        // 修改/删除时需要
  name: string;              // 主体名称
  assetType?: string;        // 主体类型：character/prop/scene
  role?: string;             // 角色类型（仅角色类型有效）
  description: string;       // 主体描述
}

/**
 * 主体设定 AI 响应（结构化输出）
 */
export interface SubjectAIResponse {
  message: string;           // AI 的文字回复
  actions: SubjectActionCard[];  // 操作卡片列表
}

/**
 * 分集大纲操作类型（复用 SubjectActionType）
 */
export type OutlineActionType = SubjectActionType;
export const OutlineActionTypeLabels = SubjectActionTypeLabels;
export const OutlineActionTypeColors = SubjectActionTypeColors;

/**
 * 分集大纲操作卡片
 */
export interface OutlineActionCard {
  actionType: OutlineActionType;
  outlineId?: string;        // 修改/删除时需要
  episodeNumber: number;     // 集数
  title: string;             // 标题
  synopsis: string;          // 简介（一两句话）
}

/**
 * 分集大纲 AI 响应（结构化输出）
 */
export interface OutlineAIResponse {
  message: string;           // AI 的文字回复
  actions: OutlineActionCard[];  // 操作卡片列表
}

/**
 * 设定 AI 响应（故事梗概、世界观、写作风格）
 */
export interface SettingAIResponse {
  message: string;           // AI 的文字回复
  content: string;           // 生成的设定内容
}

/**
 * 剧本操作类型
 */
export enum ScriptActionType {
  GENERATE = 'generate',     // 生成剧本内容
  REVISE = 'revise'          // 修改剧本内容
}

export const ScriptActionTypeLabels: I18nLabels<ScriptActionType> = {
  [ScriptActionType.GENERATE]: { [Locale.ZH_CN]: '生成', [Locale.EN_US]: 'Generate' },
  [ScriptActionType.REVISE]: { [Locale.ZH_CN]: '修改', [Locale.EN_US]: 'Revise' },
};

/**
 * 剧本操作卡片
 */
export interface ScriptActionCard {
  actionType: ScriptActionType;
  title: string;             // 章节标题（可选修改）
  content: string;           // 剧本内容
  summary?: string;          // 内容摘要
}

/**
 * 剧本 AI 响应（结构化输出）
 */
export interface ScriptAIResponse {
  message: string;           // AI 的文字回复
  actions: ScriptActionCard[];  // 操作卡片列表
}
